import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {isLeafTypeDate, isLeafTypeNumerical} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {JSONValue} from 'util/Types';

import {
  DisableFunction,
  DisableFunctionParams,
  FIELD_ORDER_UI_SCHEMA_KEY,
  FieldUISchemaWithStandardComponent,
  isFieldUISchemaWithCustomComponent,
  isFieldUISchemaWithStandardComponent,
  isShapeUISchema,
  ShapeUISchema,
  StepConfigUISchema,
} from '../StepConfigEditorTypes';
import {maybeGetValueFromConfig} from './StepConfigSettingsUtils';

const X_AXIS_COLUMN_NAME_PATH = ['chartConfig', 'parameters', 'xAxisColumnName'];

export function isEditorDisabled({
  uiSchema,
  config,
  columns,
}: DisableFunctionParams & {
  uiSchema: FieldUISchemaWithStandardComponent;
}): boolean {
  if (!uiSchema.disableFn) {
    return false;
  }
  return uiSchema.disableFn({config, columns});
}

function getColumnAtPath(
  config: Record<string, JSONValue>,
  columns: Array<DataFrameColumnDef>,
  configShapePath: Array<string>
): DataFrameColumnDef | undefined {
  const columnSystemName = maybeGetValueFromConfig(config, configShapePath);
  return columns.find((col) => col.systemName === columnSystemName);
}

/**
 * True when the selected x-axis column is numeric (Decimal/Integer). Shaped as a condition function
 * (positional config, columns) for use with CONDITIONAL_UI_SCHEMA_KEY.
 */
export function isXAxisColumnNameNumeric(
  config: Record<string, JSONValue>,
  columns: Array<DataFrameColumnDef>
): boolean {
  const column = getColumnAtPath(config, columns, X_AXIS_COLUMN_NAME_PATH);
  return column != null && isLeafTypeNumerical(column.type.leafType);
}

/** True when the selected x-axis column is a date/datetime column. */
export function isXAxisColumnNameDate(
  config: Record<string, JSONValue>,
  columns: Array<DataFrameColumnDef>
): boolean {
  const column = getColumnAtPath(config, columns, X_AXIS_COLUMN_NAME_PATH);
  return column != null && isLeafTypeDate(column.type.leafType);
}

function isConfigFieldNull({
  config,
  configShapePath,
}: DisableFunctionParams & {configShapePath: Array<string>}): boolean {
  const value = maybeGetValueFromConfig(config, configShapePath);
  return value == null;
}

export function isSeriesColumnNameNull({config, columns}: DisableFunctionParams): boolean {
  return isConfigFieldNull({
    config,
    columns,
    configShapePath: ['chartConfig', 'parameters', 'seriesColumnName'],
  });
}

const alwaysDisabled: DisableFunction = () => true;

function disableUISchema(uiSchema: StepConfigUISchema | undefined): StepConfigUISchema | undefined {
  if (uiSchema == null) {
    return uiSchema;
  }
  if (isShapeUISchema(uiSchema)) {
    return {
      ...uiSchema,
      ...Object.fromEntries(
        uiSchema[FIELD_ORDER_UI_SCHEMA_KEY].map((fieldName) => [
          fieldName,
          disableUISchema(uiSchema[fieldName]),
        ])
      ),
    } as ShapeUISchema;
  }
  if (isFieldUISchemaWithStandardComponent(uiSchema) || isFieldUISchemaWithCustomComponent(uiSchema)) {
    return {...uiSchema, disableFn: alwaysDisabled};
  }
  return uiSchema;
}

/** Returns the UI schema with the given top-level fields, and any fields nested in them, disabled. */
export function disableUISchemaFields(
  uiSchema: StepConfigUISchema,
  fieldNames: ReadonlyArray<string>
): StepConfigUISchema {
  if (!isShapeUISchema(uiSchema)) {
    return uiSchema;
  }
  return {
    ...uiSchema,
    ...Object.fromEntries(
      fieldNames
        .filter((fieldName) => uiSchema[fieldName] != null)
        .map((fieldName) => [fieldName, disableUISchema(uiSchema[fieldName])])
    ),
  } as ShapeUISchema;
}
